import { gql, useQuery } from '@apollo/client'
import orderBy from 'lodash/orderBy'
import { useEffect, useMemo, useState } from 'react'

import {
  ArtistMembershipPurchaseProceedQuery,
  ArtistMembershipPurchaseProceedQueryVariables,
  ArtistMembershipSubscriptionDetailFragmentDoc,
  ArtistWithPatronFragmentDoc,
  PaymentDetailFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import {
  getPrefrenceObject,
  removePrefernce,
  setPreference,
} from '@/core/native/storage'

import { Action, State } from './template'
import {
  ArtistMembershipPurchaseInput,
  ArtistMembershipPurchaseValues,
  MembershipPlan,
} from './template/ArtistMembershipPurchaseForm'
/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { router, generatePath, routerPages } =
    useRouter<{ artistId: string; productId: string }>()
  const [initialPlan, setInitialPlan] =
    useState<ArtistMembershipPurchaseValues>()

  const artistId =
    (config.applicationType === 'standalone'
      ? config.artistId
      : router.query.artistId) ?? '#'

  const { data, loading } = useQuery<
    ArtistMembershipPurchaseProceedQuery,
    ArtistMembershipPurchaseProceedQueryVariables
  >(QUERY, { variables: { id: artistId } })

  const plans = useMemo<MembershipPlan[]>(() => {
    if (!data?.artist?.subscription?.products?.length) {
      return []
    }

    const product = data?.artist?.subscription?.products.find(
      ({ productId }) => router.query.productId === productId
    )
    if (!product || !product.plans?.length) {
      return []
    }

    return orderBy(
      product.plans.map(plan => ({
        interval: plan.interval,
        amount: plan.amount,
        formattedAmount: plan.formattedAmount ?? '0',
        annualDiscountText: product.annualDiscountText ?? '',
        planId: plan.planId,
      })),
      ['interval'],
      ['desc']
    )
  }, [data?.artist?.subscription?.products, router.query.productId])

  useEffect(() => {
    ;(async () => {
      const v = await getPrefrenceObject<ArtistMembershipPurchaseInput>(
        'artistMembershipPurchaseInput'
      )
      if (plans?.find(plan => plan.planId === v.planId)) {
        setInitialPlan({ interval: v.interval, paymentMethod: v.paymentMethod })
        await removePrefernce('artistMembershipPurchaseInput')
      }
    })()
  }, [plans])

  return {
    state: {
      loading,
      artistId,
      plans,
      paymentSource: data?.user?.credit ?? null,
      paymentFees: null,
      initialPlan,
      /** アーティスト毎に設定されているメンバーシップのキャリア決済加入が有効かどうか */
      enableMembershipCarrierPayment:
        !!data?.artist?.subscription?.carrierPayment,
      joined: !!data?.artist?.patron,
    },
    actions: {
      async onSubmit(values: ArtistMembershipPurchaseValues) {
        const planId = plans?.find(
          plan => plan.interval === values.interval
        )?.planId
        await setPreference('artistMembershipPurchaseInput', {
          ...values,
          planId,
        })
        return router.replace(
          generatePath(routerPages.ArtistMembershipPurchaseConfirmation, {
            artistId,
            productId: router.query.productId,
          })
        )
      },
    },
  }
})

const QUERY = gql`
  query ArtistMembershipPurchaseProceed($id: ID) {
    artist(id: $id) {
      id
      ...ArtistMembershipSubscriptionDetail
      ...ArtistWithPatron
    }
    user {
      id
      credit {
        ...PaymentDetail
      }
    }
  }
  ${ArtistMembershipSubscriptionDetailFragmentDoc}
  ${ArtistWithPatronFragmentDoc}
  ${PaymentDetailFragmentDoc}
`
